// The Karpathy quote slide — shown at both `slide-vibe-coding-quote` (opening) and
// `slide-quote-return` (bookending the vibe-coding framing before the tournament hand-off). One
// component, one slide, revisited twice (`slide-chrome.html` panel 2's own caption: "so it is one
// slide the cue list revisits, not two slides").
//
// The script bolds **see** / **say** / **run** / **mostly works** (2026-08-09 edit) — those four
// are the talk's whole thesis and the wrap-up calls back to them by name. The markers stay in the
// copy (`content.ts`) and are split here, rather than the copy being chopped into segments in a
// data file that then reads as neither a sentence nor markup.
//
// Its heading ("What is vibe coding?") went with every other slide heading on 2026-08-11: the
// presenter asks the question, the slide answers it (user story 2). The quote is still centred in
// the frame — `SlideFrame` keys that on `center` now rather than on having had a title.

import type { ReactElement } from 'react';
import { SlideFrame } from './SlideFrame';
import { SLIDE_SCALE } from './scale';
import { KARPATHY_ATTRIBUTION, KARPATHY_QUOTE } from './content';

/** Splits on `**` — odd-indexed segments are the emphasised ones. Not a markdown renderer: the
 * only markup the deck's copy carries is this one marker. */
function emphasised(paragraph: string): ReactElement[] {
  return paragraph.split('**').map((segment, index) =>
    index % 2 === 1 ? (
      // eslint-disable-next-line react/no-array-index-key
      <strong key={index} style={{ color: 'var(--deck-accent)', fontWeight: 600 }}>
        {segment}
      </strong>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index}>{segment}</span>
    ),
  );
}

export function QuoteSlide(): ReactElement {
  return (
    <SlideFrame>
      <blockquote
        className="demo-stagger"
        style={{
          margin: 0,
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
          fontSize: SLIDE_SCALE.quote,
          color: 'var(--deck-ink)',
          lineHeight: 1.4,
          borderLeft: '6px solid var(--deck-accent)',
          paddingLeft: 48,
          maxWidth: '92%',
        }}
      >
        {KARPATHY_QUOTE.map((paragraph, index) => (
          <p
            key={paragraph}
            style={{ margin: index === KARPATHY_QUOTE.length - 1 ? 0 : '0 0 32px' }}
          >
            {emphasised(paragraph)}
          </p>
        ))}
      </blockquote>
      <div
        style={{
          fontSize: SLIDE_SCALE.caption,
          fontWeight: 600,
          color: 'var(--deck-caption)',
          fontFamily: 'var(--font-body)',
          paddingLeft: 54,
        }}
      >
        {KARPATHY_ATTRIBUTION}
      </div>
    </SlideFrame>
  );
}
